// Page de gestion des médias de la boutique - Photos hero et galerie
import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Image, Upload, X, GripVertical, Save, RefreshCw, Info,
  Plus, Trash2, Eye
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Tailles recommandées pour les images
const IMAGE_SPECS = {
  hero: {
    width: 1920,
    height: 600,
    ratio: '3.2:1',
    description: 'Image bannière principale (1920x600px)'
  },
  gallery: {
    width: 800,
    height: 600,
    ratio: '4:3',
    description: 'Images de galerie (800x600px)'
  }
};

export default function BoutiqueMediasPage() {
  const { user } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const heroInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [heroImages, setHeroImages] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchMedias();
  }, []);

  const fetchMedias = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/shop-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHeroImages(data.hero_images || []);
        setGalleryImages(data.gallery_images || []);
      }
    } catch (e) {
      console.error('Error fetching medias:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleHeroUpload = (e) => {
    const files = Array.from(e.target.files);
    if (heroImages.length + files.length > 3) {
      toast.error('Maximum 3 images hero');
      return;
    }

    const newImages = files.map(file => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      name: file.name
    }));
    
    setHeroImages([...heroImages, ...newImages]);
    toast.success(`${files.length} image(s) ajoutée(s)`);
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    if (galleryImages.length + files.length > 12) {
      toast.error('Maximum 12 images dans la galerie');
      return;
    }

    const newImages = files.map(file => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      name: file.name
    }));
    
    setGalleryImages([...galleryImages, ...newImages]);
    toast.success(`${files.length} image(s) ajoutée(s)`);
  };

  const removeHeroImage = (index) => {
    setHeroImages(heroImages.filter((_, i) => i !== index));
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/vendor/shop-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hero_images: heroImages.map(img => img.url),
          gallery_images: galleryImages.map(img => img.url)
        })
      });

      if (res.ok) {
        toast.success('Médias sauvegardés !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Photos & Médias</h1>
          <p className="text-gray-500 mt-1">
            Gère les images de ta boutique
          </p>
        </div>
        
        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? (
            <RefreshCw size={18} className="mr-2 animate-spin" />
          ) : (
            <Save size={18} className="mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>

      {/* Hero Images Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Images Hero</h2>
            <p className="text-sm text-gray-500">
              Ces images apparaissent en bannière sur ta boutique (max 3)
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
            <Info size={16} />
            <span>{IMAGE_SPECS.hero.ratio} • {IMAGE_SPECS.hero.width}x{IMAGE_SPECS.hero.height}px</span>
          </div>
        </div>

        {/* Hero Images Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {heroImages.map((image, index) => (
            <motion.div
              key={image.id || index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-[3/1] rounded-lg overflow-hidden bg-gray-100 group"
            >
              <img
                src={image.url || image}
                alt={`Hero ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => removeHeroImage(index)}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <Trash2 size={18} />
                </button>
              </div>
              <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                Image {index + 1}
              </div>
            </motion.div>
          ))}

          {heroImages.length < 3 && (
            <button
              onClick={() => heroInputRef.current?.click()}
              className="aspect-[3/1] rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
            >
              <Plus size={32} className="text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Ajouter une image hero</span>
              <span className="text-xs text-gray-400 mt-1">1920 x 600px recommandé</span>
            </button>
          )}
        </div>

        <input
          ref={heroInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleHeroUpload}
          className="hidden"
        />

        {heroImages.length === 0 && (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
            <Image size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune image hero</p>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => heroInputRef.current?.click()}
            >
              <Upload size={16} className="mr-2" />
              Ajouter des images
            </Button>
          </div>
        )}
      </motion.div>

      {/* Gallery Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Galerie de réalisations</h2>
            <p className="text-sm text-gray-500">
              Présente tes travaux et réalisations (max 12 photos)
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
            <Info size={16} />
            <span>{IMAGE_SPECS.gallery.ratio} • {IMAGE_SPECS.gallery.width}x{IMAGE_SPECS.gallery.height}px</span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id || index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 group cursor-move"
            >
              <img
                src={image.url || image}
                alt={`Galerie ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button className="p-2 bg-white/20 text-white rounded-lg cursor-move">
                  <GripVertical size={18} />
                </button>
                <button
                  onClick={() => removeGalleryImage(index)}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </motion.div>
          ))}

          {galleryImages.length < 12 && (
            <button
              onClick={() => galleryInputRef.current?.click()}
              className="aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
            >
              <Plus size={24} className="text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">Ajouter</span>
            </button>
          )}
        </div>

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleGalleryUpload}
          className="hidden"
        />

        {galleryImages.length === 0 && (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
            <Image size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune photo dans la galerie</p>
            <p className="text-sm text-gray-400 mb-3">
              Ajoute des photos de tes réalisations pour convaincre tes clients
            </p>
            <Button
              variant="outline"
              onClick={() => galleryInputRef.current?.click()}
            >
              <Upload size={16} className="mr-2" />
              Ajouter des photos
            </Button>
          </div>
        )}
      </motion.div>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 rounded-xl p-6 border border-blue-100"
      >
        <h3 className="font-semibold text-blue-900 mb-3">
          💡 Conseils pour des photos efficaces
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• <strong>Qualité</strong> : Utilisez des photos en haute résolution, bien éclairées</li>
          <li>• <strong>Avant/Après</strong> : Montre la transformation de tes chantiers</li>
          <li>• <strong>Détails</strong> : Photographiez les finitions et le travail soigné</li>
          <li>• <strong>Variété</strong> : Présentez différents types de projets</li>
          <li>• <strong>Équipe</strong> : Inclus des photos de toi et ton équipe au travail</li>
        </ul>
      </motion.div>
    </div>
  );
}
